/**
 * Stamped reviews accessibility patches (contrast handled in CSS).
 */
(function () {
  const LIVE_REGION_ID = 'stamped-a11y-live-region';

  function ensureLiveRegion() {
    let region = document.getElementById(LIVE_REGION_ID);
    if (region) return region;

    region = document.createElement('div');
    region.id = LIVE_REGION_ID;
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    document.body.appendChild(region);
    return region;
  }

  function announce(message) {
    if (!message) return;
    const region = ensureLiveRegion();
    region.textContent = '';
    window.requestAnimationFrame(() => {
      region.textContent = message;
    });
  }

  function starLabel(value) {
    return `${value} star${value === 1 ? '' : 's'}`;
  }

  function setStarTabState(stars, activeIndex) {
    stars.forEach((star, index) => {
      star.setAttribute('tabindex', index === activeIndex ? '0' : '-1');
    });
  }

  function getActiveStarIndex(stars) {
    const checked = stars.findIndex((star) => star.getAttribute('aria-checked') === 'true');
    if (checked >= 0) return checked;
    return 0;
  }

  function enhanceStarRatings() {
    document
      .querySelectorAll('.new-review-form .stamped-starrating, .edit-review-form .stamped-starrating')
      .forEach((group) => {
        if (group.dataset.a11yEnhanced === 'true') return;

        const stars = [...group.querySelectorAll('a[data-value]')];
        if (!stars.length) return;

        const form = group.closest('.new-review-form, .edit-review-form');
        const hiddenInput = form?.querySelector("input[name='reviewRating']");
        const label = form?.querySelector('label[for="reviewRating"]');

        group.setAttribute('role', 'radiogroup');
        group.setAttribute('data-a11y-enhanced', 'true');

        if (label) {
          if (!label.id) label.id = `stamped-review-rating-label-${Math.random().toString(36).slice(2, 8)}`;
          group.setAttribute('aria-labelledby', label.id);
        } else {
          group.setAttribute('aria-label', 'Rating');
        }

        if (hiddenInput) {
          hiddenInput.setAttribute('tabindex', '-1');
          hiddenInput.setAttribute('aria-hidden', 'true');
        }

        const selectedValue = parseInt(hiddenInput?.value || group.getAttribute('data-rating') || '0', 10);
        const activeIndex = selectedValue > 0 ? selectedValue - 1 : 0;
        setStarTabState(stars, activeIndex);

        stars.forEach((star, index) => {
          if (star.dataset.a11yStarEnhanced === 'true') return;

          const value = parseInt(star.getAttribute('data-value'), 10) || index + 1;
          star.dataset.a11yStarEnhanced = 'true';
          star.removeAttribute('href');
          star.setAttribute('role', 'radio');
          star.setAttribute('aria-label', starLabel(value));
          star.setAttribute('aria-checked', value === selectedValue ? 'true' : 'false');

          star.addEventListener('click', () => {
            if (hiddenInput) {
              hiddenInput.value = String(value);
              hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
            group.setAttribute('data-rating', String(value));

            stars.forEach((item, itemIndex) => {
              item.setAttribute('aria-checked', itemIndex === index ? 'true' : 'false');
            });
            setStarTabState(stars, index);
          });

          star.addEventListener('keydown', (event) => {
            if (event.key === 'Tab') return;

            const currentIndex = stars.indexOf(star);
            let nextIndex = null;

            if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
              nextIndex = Math.min(stars.length - 1, currentIndex + 1);
            } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
              nextIndex = Math.max(0, currentIndex - 1);
            } else if (event.key === 'Home') {
              nextIndex = 0;
            } else if (event.key === 'End') {
              nextIndex = stars.length - 1;
            } else if (event.key === ' ' || event.key === 'Enter') {
              event.preventDefault();
              star.click();
              return;
            }

            if (nextIndex === null) return;

            event.preventDefault();
            setStarTabState(stars, nextIndex);
            stars[nextIndex].focus();
          });
        });
      });
  }

  function getVoteCount(node) {
    const count = node.getAttribute('data-count');
    if (count) return count;
    const match = (node.textContent || '').match(/(\d+)/);
    return match ? match[1] : '0';
  }

  function enhanceVoteButtons() {
    const selectors = [
      '.stamped-thumbs-up',
      '.stamped-thumbs-down',
      '.stamped-ugc-modal-vote-up',
      '.stamped-ugc-modal-vote-down',
      '#stamped-rating-holder a.stamped-thumbs-up',
      '#stamped-rating-holder a.stamped-thumbs-down',
    ];

    document.querySelectorAll(selectors.join(', ')).forEach((button) => {
      if (button.dataset.a11yVoteEnhanced === 'true') return;
      button.dataset.a11yVoteEnhanced = 'true';

      if (button.tagName.toLowerCase() === 'a') {
        button.setAttribute('role', 'button');
      }

      if (!button.hasAttribute('tabindex')) {
        button.setAttribute('tabindex', '0');
      }

      const isHelpful =
        button.classList.contains('stamped-thumbs-up') || button.classList.contains('stamped-ugc-modal-vote-up');
      const baseLabel = isHelpful ? 'Rate review as helpful' : 'Rate review as not helpful';

      const updateLabel = (announceChange) => {
        const count = getVoteCount(button);
        const nextLabel = `${baseLabel}, ${count} vote${count === '1' ? '' : 's'}`;
        const previousLabel = button.getAttribute('aria-label');
        if (previousLabel !== nextLabel) button.setAttribute('aria-label', nextLabel);

        if (announceChange && previousLabel && previousLabel !== nextLabel) {
          announce(nextLabel);
        }
      };

      updateLabel(false);

      const observer = new MutationObserver(() => updateLabel(true));
      // Watch vote data only; observing aria-label feeds our own update back into this observer.
      observer.observe(button, {
        childList: true, subtree: true, characterData: true,
        attributes: true, attributeFilter: ['data-count'],
      });

      button.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          button.click();
        }
      });
    });
  }

  function enhanceStampedWidget() {
    enhanceStarRatings();
    enhanceVoteButtons();
  }

  document.addEventListener('stamped:reviews:loaded', enhanceStampedWidget);
  document.addEventListener('stamped:questions:loaded', enhanceStampedWidget);

  document.addEventListener('click', (event) => {
    if (
      event.target.closest(
        '.stamped-summary-actions-newreview, .stamped-summary-actions-newquestion, .stamped-summary-actions-clear'
      )
    ) {
      window.setTimeout(enhanceStampedWidget, 250);
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    enhanceStampedWidget();

    const observer = new MutationObserver(() => enhanceStampedWidget());
    const widget = document.querySelector('#stamped-main-widget, [id*="stamped-main-widget"]');
    if (widget) {
      observer.observe(widget, { childList: true, subtree: true });
    } else {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  });
})();
